/**
 * Google Calendar Data Management
 *
 * Provides React Query client and persistence for all Google Calendar data
 * including accounts, access tokens, calendars, and events following
 * NotePlan Web context patterns.
 */

import { createContext } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import type { AccessTokenResponse } from '../../lib/google/types'

// ============================================================================
// Context Definition
// ============================================================================

/**
 * React Query context for Google Calendar data management
 * Provides isolated query client for caching and persistence of all Google Calendar data
 * including accounts, access tokens, calendars, and events
 */
export const googleCalendarDataContext = createContext<QueryClient | undefined>(
  undefined
)

// ============================================================================
// Configuration
// ============================================================================

/**
 * Maximum age for Google Calendar data cache (55 minutes)
 * Slightly less than 1 hour to ensure access tokens are refreshed before expiration
 * and other Google Calendar data remains reasonably fresh
 */
export const googleCalendarDataMaxAge = 1000 * 60 * 55 // 55 minutes

/**
 * Dedicated React Query client for Google Calendar data management
 * Configured with appropriate caching and refetch policies for all Google Calendar data
 * including accounts, access tokens, calendars, and events
 */
export const googleCalendarDataQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: googleCalendarDataMaxAge,
      staleTime: googleCalendarDataMaxAge,
      // disable too much refetching
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 3,
    },
  },
})

/**
 * Local storage persister for Google Calendar data caching
 * Persists all Google Calendar data (accounts, tokens, calendars, events) across browser sessions
 */
export const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'googleCalendarData',
})

// ============================================================================
// Token Validation
// ============================================================================

/**
 * Type guard to check if data matches AccessTokenResponse structure
 */
function isAccessTokenResponse(data: unknown): data is AccessTokenResponse {
  return (
    typeof data === 'object' &&
    // eslint-disable-next-line unicorn/no-null -- null can be also an object
    data != null &&
    'token' in data &&
    'expires' in data &&
    'account_id' in data &&
    typeof (data as AccessTokenResponse).token === 'string' &&
    typeof (data as AccessTokenResponse).expires === 'number' &&
    typeof (data as AccessTokenResponse).account_id === 'string'
  )
}

/**
 * Checks if a query key matches the access token pattern
 */
function isAccessTokenQueryKey(queryKey: readonly unknown[]): boolean {
  return (
    Array.isArray(queryKey) &&
    queryKey.length === 3 &&
    typeof queryKey[0] === 'string' && // userId
    queryKey[1] === 'access_token' &&
    typeof queryKey[2] === 'string' // accountId
  )
}

/**
 * Validates cached access tokens
 * Initiates refresh for expired tokens to ensure data freshness
 * Called on successful persistence restoration
 */
export function validateToken() {
  const allQueries =
    googleCalendarDataQueryClient.getQueriesData<AccessTokenResponse>({
      type: 'all',
    })

  // Filter to only process access token queries
  const accessTokenQueries = allQueries.filter(
    ([queryKey, data]) =>
      isAccessTokenQueryKey(queryKey) && isAccessTokenResponse(data)
  )

  for (const [queryKey, accessToken] of accessTokenQueries) {
    // in case we got an old token, initiate a refetch
    if (accessToken && accessToken.expires <= Date.now()) {
      void googleCalendarDataQueryClient.invalidateQueries([queryKey])
    }
  }
}
